/**
 * Client-side CSRF Token Utility
 * 
 * Provides utilities for fetching and including CSRF tokens in API requests
 */

let csrfTokenCache: string | null = null
let csrfTokenPromise: Promise<string> | null = null

/**
 * Get auth headers from localStorage (similar to use-data-fetch.ts)
 * Returns Authorization header if available
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}
  
  try {
    if (typeof window === 'undefined') {
      return headers
    }

    // Get Supabase URL to construct storage key
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
    const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
    const projectRef = match ? match[1] : 'default'
    const storageKey = `sb-${projectRef}-auth-token`
    
    // Try to get token from localStorage
    const storedSession = localStorage.getItem(storageKey)
    if (storedSession) {
      try {
        const parsedSession = JSON.parse(storedSession)
        const accessToken = parsedSession?.access_token || parsedSession?.currentSession?.access_token
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  } catch (e) {
    // Ignore errors - continue without auth header
  }
  
  return headers
}

/**
 * Get CSRF token from server
 * Caches the token to avoid repeated requests
 */
export async function getCsrfToken(): Promise<string> {
  // Return cached token if available
  if (csrfTokenCache) {
    return csrfTokenCache
  }

  // Return existing promise if already fetching
  if (csrfTokenPromise) {
    return csrfTokenPromise
  }

  // Get auth headers for authenticated request
  const authHeaders = await getAuthHeaders()

  // Fetch token from server with auth headers
  csrfTokenPromise = fetch('/api/csrf-token', {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...authHeaders,
    },
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error('Failed to get CSRF token')
      }
      const data = await response.json()
      if (data.success && data.token) {
        csrfTokenCache = data.token
        return data.token
      }
      throw new Error('Invalid CSRF token response')
    })
    .catch((error) => {
      // Clear promise on error so we can retry
      csrfTokenPromise = null
      throw error
    })

  return csrfTokenPromise
}

/**
 * Clear CSRF token cache (useful after logout or token refresh)
 */
export function clearCsrfTokenCache(): void {
  csrfTokenCache = null
  csrfTokenPromise = null
}

/**
 * Create fetch options with CSRF token header
 * Use this for POST, PATCH, PUT, DELETE requests
 * 
 * @example
 * ```typescript
 * const response = await fetch('/api/profile', {
 *   method: 'PATCH',
 *   ...await withCsrfToken({
 *     credentials: 'include',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify(data),
 *   }),
 * })
 * ```
 */
export async function withCsrfToken(
  options: RequestInit = {}
): Promise<RequestInit> {
  const token = await getCsrfToken()
  
  return {
    ...options,
    headers: {
      ...options.headers,
      'X-CSRF-Token': token,
    },
  }
}

/**
 * Enhanced fetch wrapper that automatically includes CSRF token for state-changing methods
 * 
 * @example
 * ```typescript
 * const response = await csrfFetch('/api/profile', {
 *   method: 'PATCH',
 *   credentials: 'include',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify(data),
 * })
 * ```
 */
export async function csrfFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const method = options.method?.toUpperCase() || 'GET'
  
  // Only include CSRF token for state-changing methods
  if (['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
    const token = await getCsrfToken()
    options.headers = {
      ...options.headers,
      'X-CSRF-Token': token,
    }
  }

  return fetch(url, options)
}

